import type { RouterHistory } from "@tanstack/react-router";

/**
 * Pathname of every history entry visited in this tab, keyed by TanStack's `__TSR_index`.
 * Lets links know what the previous entry is, so they can go back to it instead of pushing it
 * again (which would drop the search params the user had selected on that page).
 */
const pathnamesByIndex = new Map<number, string>();

const normalizePathname = (pathname: string) => (pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname);

const createHistoryKey = () => Math.random().toString(36).slice(2, 10);

export function trackNavigationHistory(history: RouterHistory) {
    let lastIndex = 0;

    const record = () => {
        const { state, pathname } = history.location;

        // Code outside the router can replace the history state and drop TanStack's index (the auth
        // provider does `replaceState({})` when stripping `auth_code` after login). Every later entry
        // then gets a NaN index, so restore it; the replace notifies us again with the repaired state.
        if (!Number.isInteger(state.__TSR_index)) {
            const key = state.__TSR_key ?? state.key ?? createHistoryKey();
            window.history.replaceState(
                { ...state, key, __TSR_key: key, __TSR_index: lastIndex },
                "",
                window.location.href
            );
            return;
        }

        lastIndex = state.__TSR_index;
        pathnamesByIndex.set(lastIndex, normalizePathname(pathname));
    };

    record();
    history.subscribe(record);
}

/** True when `pathname` is the page right before the current one in the history stack. */
export function isPreviousHistoryEntry(history: RouterHistory, pathname: string) {
    if (!history.canGoBack()) {
        return false;
    }

    const target = normalizePathname(pathname);
    const current = normalizePathname(history.location.pathname);
    const previous = pathnamesByIndex.get(history.location.state.__TSR_index - 1);

    return previous !== undefined && previous === target && current !== target;
}
